import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import type { NtCourse, NtRoster, NtTerm, NtDay, NtAttendanceStatus } from '../../core/types'
import { useAttendance, markAttendance } from './model'
import { STATUSES, normalizeStatus, FRIDAY, termDay } from './attendanceLogic'
import { sessionLabel, currentPhase } from './checklist'

/** For practice before placement: nobody real, nothing saved. */
const SAMPLE_ROSTER: NtRoster = {
  id: 'sample', termId: '', courseId: 'sample', teacherUid: '', block: 'AM',
  students: [
    { studentId: 's1', name: 'Sample Student 1', grade: 9 }, { studentId: 's2', name: 'Sample Student 2', grade: 10 },
    { studentId: 's3', name: 'Sample Student 3', grade: 11 }, { studentId: 's4', name: 'Sample Student 4', grade: 12 },
  ],
}
import { useNorseTerm } from './NorseTermData'
import { Empty, ErrorState } from '../../ui/States'

/**
 * Attendance, during the week itself.
 *
 * Everyone defaults to present and you mark the exceptions — the opposite of
 * marking 16 kids one at a time. This is used standing up, in a shop or a
 * kitchen or on a riverbank, by somebody who has a fire going: four big
 * labelled buttons per student rather than tap-to-cycle, because cycling is
 * how a kid gets marked "tardy" by a thumb that meant "absent".
 *
 * Saving writes every rostered student's mark, so the office can tell "taken,
 * everyone here" from "not taken yet". Notes go to the office and admin.
 *
 * One document per course per day. Per-student would be 800 writes across the
 * week from phones with bad signal, and none of them are ever read alone.
 */
export function Attendance({ term, rosters: realRosters, courses, who }: {
  term: NtTerm
  rosters: NtRoster[]
  courses: NtCourse[]
  who: string
}) {
  // ?day=0..3&course= come from the steps: "Take attendance: Tuesday
  // morning" opens on exactly that class.
  const [params] = useSearchParams()
  let rosters = realRosters
  const { mySessions: realSessions, myAttendanceTaken } = useNorseTerm()
  // Before the week, practice: the real screen, nothing saved. With no roster
  // yet, a few sample students, so a teacher can see what it will be like.
  const practice = currentPhase(term) === 'prep'
  const sample = practice && rosters.length === 0
  if (sample) rosters = [SAMPLE_ROSTER]
  const mySessions = sample ? [{ courseId: SAMPLE_ROSTER.courseId, block: 'AM' as const, title: 'Your course', friday: false }] : realSessions
  const title = (id: string) => courses.find((c) => c.id === id)?.title ?? 'Your course'
  // Every class this teacher has, in the order of the week: Tuesday morning,
  // Tuesday afternoon, … Friday morning only for courses with artifact makers.
  const sessions = ([0, 1, 2, 3] as NtDay[]).flatMap((d) => {
    const here = mySessions.filter((x) => d !== FRIDAY || x.friday)
    return here.map((x) => ({ day: d, courseId: x.courseId,
      label: `${sessionLabel(d, x.block)}${d === FRIDAY && here.length > 1 ? `, ${x.title}` : ''}` }))
  })
  const dayParam = Number(params.get('day'))
  const wantDay = Number.isInteger(dayParam) && dayParam >= 0 && dayParam <= 3 ? dayParam : null
  const wantCourse = params.get('course')
  const today = termDay(term)
  const first = sessions.find((x) => x.day === wantDay && (!wantCourse || x.courseId === wantCourse))
    ?? sessions.find((x) => x.day === (wantDay ?? today) && !myAttendanceTaken.has(`${x.courseId}_${x.day}`))
    ?? sessions.find((x) => x.day === (wantDay ?? today))
    ?? sessions[0]
  const [pick, setPick] = useState<{ day: NtDay; courseId: string } | null>(null)
  const at = pick ?? first
  const roster = at && rosters.find((r) => r.courseId === at.courseId)

  if (rosters.length === 0) {
    return (
      <Empty
        title="No roster yet"
        body="Rosters appear here once admin has run placement. Until then there is nobody to mark."
      />
    )
  }

  return (
    <>
      {practice && (
        <div className="notice notice--info" role="status" style={{ marginTop: 'var(--s-2)' }}>
          <span aria-hidden="true">i</span>
          <div>
            <strong>Practice.</strong> Attendance opens {term.startDate ? <>on <strong>{new Date(term.startDate + 'T12:00:00').toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}</strong></> : 'when Norse Term starts'}.
            Until then, try it: tap the marks and add a note. Nothing is saved.
            {sample && ' These are sample students; your real roster appears here once placement runs.'}
          </div>
        </div>
      )}
      <div className="chips" style={{ marginTop: 'var(--s-2)' }} role="tablist" aria-label="Which class">
        {sessions.map((x) => {
          const on = at?.day === x.day && at.courseId === x.courseId
          const done = myAttendanceTaken.has(`${x.courseId}_${x.day}`)
          return (
            <button key={`${x.day}_${x.courseId}`} role="tab" aria-selected={on} className={`chip${on ? ' is-on' : ''}`}
              onClick={() => setPick({ day: x.day, courseId: x.courseId })}>
              {x.label}{done ? ' ✓' : ''}<span className="visually-hidden">{done ? ' (taken)' : ' (not taken)'}</span>
            </button>
          )
        })}
      </div>
      {roster && at && (
        <>
          <h2 className="work__heading">
            {sessionLabel(at.day, roster.block)} · {title(roster.courseId)}
          </h2>
          <DaySheets key={`${roster.courseId}_${at.day}`} term={term} roster={roster} day={at.day} who={who} practice={practice} />
        </>
      )}
    </>
  )
}

function DaySheets({ term, roster: full, day, who, practice }: { term: NtTerm; roster: NtRoster; day: NtDay; who: string; practice: boolean }) {
  // Practice reads nothing and saves nothing (a sample roster has no course to read).
  const saved = useAttendance(practice ? '' : term.id, full.courseId)
  const { artifactOf } = useNorseTerm()
  // Friday morning is artifact prep: only the students making their artifact
  // in this course are here. Everyone else is with their other course.
  const roster: NtRoster = day === FRIDAY
    ? { ...full, students: full.students.filter((s) => artifactOf.get(s.studentId) === full.block) }
    : full
  const makingHere = new Set(full.students.filter((s) => artifactOf.get(s.studentId) === full.block).map((s) => s.studentId))
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [savedAt, setSavedAt] = useState<Date | null>(null)

  const sheet = saved[day]
  const existing: Record<string, NtAttendanceStatus> = Object.fromEntries(
    Object.entries(sheet?.marks ?? {}).map(([k, v]) => [k, normalizeStatus(v) ?? 'present']))
  const [draft, setDraft] = useState<Record<string, NtAttendanceStatus>>({})
  const [noteDraft, setNoteDraft] = useState<Record<string, string>>({})
  const [noting, setNoting] = useState<string | null>(null)
  const marks = { ...existing, ...draft }
  const notes = { ...(sheet?.notes ?? {}), ...noteDraft }
  const dirty = Object.keys(draft).length > 0 || Object.keys(noteDraft).length > 0

  const mark = (id: string, s: NtAttendanceStatus) => setDraft((d) => ({ ...d, [id]: s }))

  const save = async () => {
    if (practice) { setDraft({}); setNoteDraft({}); setNoting(null); setSavedAt(new Date()); return }
    setBusy(true); setErr(null)
    const full: Record<string, NtAttendanceStatus> = {}
    roster.students.forEach((s) => { full[s.studentId] = marks[s.studentId] ?? 'present' })
    try {
      await markAttendance(term.id, roster.courseId, day, full, notes, who)
      setDraft({}); setNoteDraft({}); setNoting(null); setSavedAt(new Date())
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)) }
    finally { setBusy(false) }
  }

  const count = (s: NtAttendanceStatus) => roster.students.filter((st) => (marks[st.studentId] ?? 'present') === s).length
  const absent = count('absent')
  const tardy = count('tardy-excused') + count('tardy-unexcused')
  const summary = [absent && `${absent} absent`, tardy && `${tardy} tardy`].filter(Boolean).join(', ') || 'all present'

  return (
    <>
      {err && <ErrorState body={`Not saved. ${err}`} />}
      {day === FRIDAY && (
        <p className="opt__hint" style={{ marginTop: 0 }}>
          Friday morning is artifact and open-house prep. Only the {roster.students.length} students making their
          artifact with you are here.
        </p>
      )}
      {day === FRIDAY && roster.students.length === 0 ? (
        <p className="muted-note">Nobody has chosen to make their artifact in this course, so there is nobody to mark Friday.</p>
      ) : <>
      <p className="opt__hint" style={{ marginTop: 0 }}>
        {sheet
          ? <>Taken{sheet.takenBy ? ` by ${sheet.takenBy}` : ''}. Change anything and save again.</>
          : <>Everyone is present unless you mark otherwise. Notes go to the office.</>}
      </p>

      <ul className="att">
        {roster.students.map((s) => {
          const st = marks[s.studentId] ?? 'present'
          const note = notes[s.studentId] ?? ''
          return (
            <li key={s.studentId} className={`att__row is-${st}`}>
              <div className="att__who">
                <div style={{ fontWeight: 610 }}>{s.name}</div>
                <div className="work__sub">Grade {s.grade}{makingHere.has(s.studentId) && day !== FRIDAY ? ' · artifact here' : ''}</div>
              </div>
              <div className="att__marks" role="radiogroup" aria-label={`${s.name}, attendance`}>
                {STATUSES.map((o) => (
                  <button key={o.k} role="radio" aria-checked={st === o.k} aria-label={o.label} title={o.label}
                    className={`att__mark tone-${o.tone}${st === o.k ? ' is-on' : ''}`}
                    onClick={() => mark(s.studentId, o.k)}>
                    <span aria-hidden="true">{o.short}</span>
                    <span className="att__mark-label">{o.label}</span>
                  </button>
                ))}
              </div>
              {noting === s.studentId ? (
                <input className="opt__input att__note" autoFocus value={note} aria-label={`Note for the office about ${s.name}`}
                  placeholder="For the office, e.g. left at 10:30, dentist"
                  onChange={(e) => setNoteDraft((n) => ({ ...n, [s.studentId]: e.target.value }))}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === 'Escape') setNoting(null) }} />
              ) : (
                <button className="linkish att__note-link" onClick={() => setNoting(s.studentId)}>
                  {note ? `Note: ${note}` : 'Add note'}
                </button>
              )}
            </li>
          )
        })}
      </ul>

      <div className="opt__bar">
        <button className="btn" onClick={save} disabled={busy || (!!sheet && !dirty)}>
          {busy ? 'Saving…' : sheet && !dirty ? `${sessionLabel(day, roster.block)} is in: ${summary}` : `Save ${sessionLabel(day, roster.block)}: ${summary}`}
        </button>
      </div>
      {savedAt && <p className="opt__hint" role="status" style={{ textAlign: 'center' }}>{practice
        ? 'Practice only: on the day, this is where it saves and the office sees it at once. Nothing was saved.'
        : `Saved at ${savedAt.toLocaleTimeString()}. The office can see it now.`}</p>}
      </>}
    </>
  )
}

/**
 * A teacher's roster: each class they have, morning before afternoon, its
 * students and grades, and who makes their open-house artifact there (they
 * are the ones with you Friday morning).
 */
export function MyRoster({ rosters, courses }: { rosters: NtRoster[]; courses: NtCourse[] }) {
  const { artifactOf } = useNorseTerm()
  if (rosters.length === 0) {
    return <Empty title="No roster yet" body="Your students appear here once admin runs placement. Until then you can practice taking attendance." />
  }
  const sorted = [...rosters].sort((a, b) => (a.block === 'PM' ? 1 : 0) - (b.block === 'PM' ? 1 : 0))
  return (
    <>
      {sorted.map((r) => {
        const c = courses.find((x) => x.id === r.courseId)
        const students = [...r.students].sort((a, b) => a.name.localeCompare(b.name))
        const makers = students.filter((s) => artifactOf.get(s.studentId) === r.block).length
        return (
          <section key={r.courseId} className="card" style={{ marginTop: 'var(--s-3)' }}>
            <h2 className="ov__h" style={{ margin: 0 }}>{r.block === 'PM' ? 'Afternoon' : 'Morning'} · {c?.title ?? 'Your course'}</h2>
            <p className="work__sub" style={{ margin: '2px 0 var(--s-3)' }}>
              {students.length} {students.length === 1 ? 'student' : 'students'}{c?.room ? ` · ${c.room}` : ''} · {makers} making their artifact with you
            </p>
            <table className="sched">
              <tbody>
                {students.map((s) => (
                  <tr key={s.studentId}>
                    <td style={{ borderTop: '1px solid var(--line)' }}><strong>{s.name}</strong></td>
                    <td style={{ borderTop: '1px solid var(--line)' }} className="work__sub">Grade {s.grade}</td>
                    <td style={{ borderTop: '1px solid var(--line)' }} className="work__sub">{artifactOf.get(s.studentId) === r.block ? 'Artifact here' : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )
      })}
      <div className="opt__bar"><Link to="/norseterm/attend" className="btn btn--ghost" style={{ width: 'auto', textDecoration: 'none' }}>Take attendance →</Link></div>
    </>
  )
}
